export const getSquashSetWinnerScore = (loserScore) => {
  const numericScore = Number(loserScore);
  if (!Number.isInteger(numericScore) || numericScore < 0) {
    return null;
  }
  return numericScore <= 9 ? 11 : numericScore + 2;
};

export const isValidSquashSetLoserScore = (value) => {
  const numericScore = Number(value);
  return Number.isInteger(numericScore) && numericScore >= 0 && numericScore <= 97;
};

export const getSquashSetPlayerName = (set, playerId) => {
  if (playerId === set.playerAId) {
    return set.playerANickname || set.playerADisplayName || 'Ismeretlen játékos';
  }
  if (playerId === set.playerBId) {
    return set.playerBNickname || set.playerBDisplayName || 'Ismeretlen játékos';
  }
  return 'Ismeretlen játékos';
};

export const getSquashSetLoserScore = (set) => {
  if (set.winnerId === set.playerAId) {
    return set.playerBScore;
  }
  if (set.winnerId === set.playerBId) {
    return set.playerAScore;
  }
  return '';
};

export const normalizeSquashSet = (setDoc) => {
  const data = setDoc.data() || {};
  return {
    id: setDoc.id,
    trainingDate: typeof data.training_date === 'string' ? data.training_date : '',
    entryBatchId: typeof data.entry_batch_id === 'string' ? data.entry_batch_id : '',
    setNumber: Number.isInteger(data.set_number) ? data.set_number : 1,
    playerAId: typeof data.player_a_id === 'string' ? data.player_a_id : '',
    playerADisplayName: typeof data.player_a_display_name === 'string' ? data.player_a_display_name : '',
    playerANickname: typeof data.player_a_nickname === 'string' ? data.player_a_nickname : '',
    playerBId: typeof data.player_b_id === 'string' ? data.player_b_id : '',
    playerBDisplayName: typeof data.player_b_display_name === 'string' ? data.player_b_display_name : '',
    playerBNickname: typeof data.player_b_nickname === 'string' ? data.player_b_nickname : '',
    winnerId: typeof data.winner_id === 'string' ? data.winner_id : '',
    loserId: typeof data.loser_id === 'string' ? data.loser_id : '',
    playerAScore: Number.isInteger(data.player_a_score) ? data.player_a_score : 0,
    playerBScore: Number.isInteger(data.player_b_score) ? data.player_b_score : 0,
    playerIds: Array.isArray(data.player_ids) ? data.player_ids.filter((id) => typeof id === 'string') : [],
    recordedByUserId: typeof data.recorded_by_user_id === 'string' ? data.recorded_by_user_id : '',
    playedAt: data.played_at,
    recordedAt: data.recorded_at || data.createdAt,
    updatedAt: data.updatedAt,
  };
};

export const sortSquashSetsNewestFirst = (left, right) => {
  const leftMillis = left.playedAt?.toMillis?.() || left.recordedAt?.toMillis?.() || 0;
  const rightMillis = right.playedAt?.toMillis?.() || right.recordedAt?.toMillis?.() || 0;
  if (leftMillis !== rightMillis) {
    return rightMillis - leftMillis;
  }
  return right.id.localeCompare(left.id);
};

const getSquashSetTimestampMillis = (set) => {
  const timestampCandidates = [set.playedAt, set.recordedAt];
  for (const timestamp of timestampCandidates) {
    const millis = timestamp?.toMillis?.()
      ?? timestamp?.toDate?.()?.getTime?.()
      ?? (timestamp instanceof Date ? timestamp.getTime() : NaN);
    if (Number.isFinite(millis)) {
      return millis;
    }
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(set.trainingDate || '')) {
    return Date.parse(`${set.trainingDate}T12:00:00Z`);
  }
  return 0;
};

const getThreeMonthsEarlier = (timestampMillis) => {
  const date = new Date(timestampMillis);
  const originalDay = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() - 3);
  const lastDayOfMonth = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    0,
  )).getUTCDate();
  date.setUTCDate(Math.min(originalDay, lastDayOfMonth));
  return date.getTime();
};

const getRoundedAverage = (values) => {
  if (values.length === 0) {
    return null;
  }
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Number(average.toFixed(1));
};

export const buildSquashHeadToHeadRows = (sets, currentUserId) => {
  if (!currentUserId) {
    return [];
  }

  const rowsByOpponent = new Map();
  sets.forEach((set) => {
    const currentUserIsPlayerA = set.playerAId === currentUserId;
    const currentUserIsPlayerB = set.playerBId === currentUserId;
    if (!currentUserIsPlayerA && !currentUserIsPlayerB) {
      return;
    }

    const opponentId = currentUserIsPlayerA ? set.playerBId : set.playerAId;
    if (!opponentId) {
      return;
    }

    if (!rowsByOpponent.has(opponentId)) {
      rowsByOpponent.set(opponentId, {
        opponentId,
        currentPlayerName: getSquashSetPlayerName(set, currentUserId),
        opponentName: getSquashSetPlayerName(set, opponentId),
        currentPlayerWins: 0,
        opponentWins: 0,
        sets: [],
      });
    }

    const row = rowsByOpponent.get(opponentId);
    if (set.winnerId === currentUserId) {
      row.currentPlayerWins += 1;
    } else if (set.winnerId === opponentId) {
      row.opponentWins += 1;
    }

    const currentPlayerScore = currentUserIsPlayerA ? set.playerAScore : set.playerBScore;
    const opponentScore = currentUserIsPlayerA ? set.playerBScore : set.playerAScore;
    if (Number.isInteger(currentPlayerScore) && Number.isInteger(opponentScore)) {
      row.sets.push({
        currentPlayerScore,
        opponentScore,
        timestampMillis: getSquashSetTimestampMillis(set),
      });
    }
  });

  return [...rowsByOpponent.values()]
    .map((row) => {
      const latestTimestampMillis = Math.max(...row.sets.map((set) => set.timestampMillis), 0);
      const windowStartMillis = latestTimestampMillis
        ? getThreeMonthsEarlier(latestTimestampMillis)
        : 0;
      const recentSets = row.sets.filter((set) => (
        latestTimestampMillis === 0
        || (set.timestampMillis >= windowStartMillis && set.timestampMillis <= latestTimestampMillis)
      ));

      return {
        opponentId: row.opponentId,
        currentPlayerName: row.currentPlayerName,
        opponentName: row.opponentName,
        currentPlayerWins: row.currentPlayerWins,
        opponentWins: row.opponentWins,
        currentPlayerPointAverage: getRoundedAverage(recentSets.map((set) => set.currentPlayerScore)),
        opponentPointAverage: getRoundedAverage(recentSets.map((set) => set.opponentScore)),
      };
    })
    .sort((left, right) => left.opponentName.localeCompare(right.opponentName, 'hu'));
};
